import React, { useState, useEffect, useRef } from 'react';
import './Gallery.css';
import Navbar from '../../Navbar/Navbar';

const LOGIN_API_URL = 'https://lfsofficial.in/api/admin_login.php';
const UPLOAD_API_URL = 'https://lfsofficial.in/api/admin_gallery_upload.php';
const LIST_API_URL = 'https://lfsofficial.in/api/admin_gallery_list.php';
const DELETE_API_URL = 'https://lfsofficial.in/api/admin_gallery_delete.php';

const CATEGORY_OPTIONS = [
  'All',
  'Annual Day',
  'Sports',
  'Science Fair',
  'Cultural Events',
  'Classroom',
  'Field Trips',
  'Achievements',
  'Festivals',
  'Other',
];

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [filterCategory, setFilterCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Lightbox
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Admin
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Upload
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileRef = useRef();

  // Delete
  const [deleteId, setDeleteId] = useState(null);

  // View mode
  const [viewMode, setViewMode] = useState('masonry'); // masonry | grid | list

  // ── fetch gallery from the database ──
  const fetchImages = async () => {
    setListLoading(true);
    setListError('');
    try {
      const response = await fetch(LIST_API_URL);
      const data = await response.json();
      if (data.success) {
        setImages(data.images);
      } else {
        setListError('Could not load gallery images.');
      }
    } catch (err) {
      setListError(`Unable to reach the server: ${err.message}`);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  // Keyboard nav in lightbox
  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') setLightboxIndex(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex, filterCategory, searchQuery]);

  const filtered = images.filter((img) => {
    const matchCat = filterCategory === 'All' || img.category === filterCategory;
    const matchSearch =
      img.caption.toLowerCase().includes(searchQuery.toLowerCase()) ||
      img.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  const nextImage = () => setLightboxIndex((i) => (i + 1) % filtered.length);
  const prevImage = () => setLightboxIndex((i) => (i - 1 + filtered.length) % filtered.length);

  // ── login (real, server-verified) ──
  const handleLogin = async () => {
    setLoginError('');
    if (!loginUser || !loginPass) {
      setLoginError('Please enter both username and password.');
      return;
    }

    setLoginLoading(true);
    try {
      const response = await fetch(LOGIN_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUser, password: loginPass }),
      });
      const data = await response.json();

      if (data.success) {
        setIsLoggedIn(true);
        setLoginError('');
      } else {
        setLoginError(data.message || 'Invalid username or password.');
      }
    } catch (err) {
      setLoginError(`Unable to reach the server: ${err.message}`);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setLoginUser('');
    setLoginPass('');
    setShowAdminModal(false);
    setPendingFiles([]);
    setUploadCategory('');
    setUploadCaption('');
    setUploadError('');
    setUploadSuccess('');
  };

  // ── file handling (client-side selection, before upload) ──
  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    validateFiles(files);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    validateFiles(files);
    e.target.value = '';
  };

  const validateFiles = (files) => {
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const valid = [];
    let error = '';
    for (const file of files) {
      if (!allowed.includes(file.type)) {
        error = `"${file.name}" is not a supported image format (JPG, PNG, WEBP, GIF only).`;
        continue;
      }
      if (file.size > 8 * 1024 * 1024) {
        error = `"${file.name}" exceeds 8 MB limit.`;
        continue;
      }
      valid.push(file);
    }
    setUploadError(error);
    if (valid.length) {
      setPendingFiles((prev) => [...prev, ...valid]);
    }
  };

  const removePending = (idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // ── upload submit (real files, sent to server one by one) ──
  const handleUpload = async () => {
    setUploadError('');
    if (!uploadCategory) { setUploadError('Please select a category.'); return; }
    if (pendingFiles.length === 0) { setUploadError('Please add at least one image.'); return; }

    setIsUploading(true);
    let successCount = 0;
    let failedNames = [];

    for (const file of pendingFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', uploadCategory);
        formData.append('caption', uploadCaption.trim());
        formData.append('username', loginUser);
        formData.append('password', loginPass);

        const response = await fetch(UPLOAD_API_URL, {
          method: 'POST',
          body: formData,
        });
        const data = await response.json();

        if (data.success) {
          successCount++;
        } else {
          failedNames.push(file.name);
        }
      } catch (err) {
        failedNames.push(file.name);
      }
    }

    setIsUploading(false);
    setPendingFiles([]);
    setUploadCaption('');
    setUploadCategory('');

    if (successCount > 0) {
      setUploadSuccess(`${successCount} image${successCount > 1 ? 's' : ''} uploaded successfully!`);
      setTimeout(() => setUploadSuccess(''), 3500);
      fetchImages(); // refresh the gallery so new images show up immediately
    }
    if (failedNames.length > 0) {
      setUploadError(`Failed to upload: ${failedNames.join(', ')}`);
    }
  };

  // ── delete (real, server-verified) ──
  const confirmDelete = (id) => setDeleteId(id);

  const handleDelete = async () => {
    try {
      const response = await fetch(DELETE_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: deleteId, username: loginUser, password: loginPass }),
      });
      const data = await response.json();

      if (data.success) {
        setImages((prev) => prev.filter((img) => img.id !== deleteId));
      } else {
        setListError(data.message || 'Could not delete this image.');
      }
    } catch (err) {
      setListError(`Unable to reach the server: ${err.message}`);
    } finally {
      setDeleteId(null);
    }
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const categoryCounts = CATEGORY_OPTIONS.reduce((acc, cat) => {
    acc[cat] = cat === 'All' ? images.length : images.filter((i) => i.category === cat).length;
    return acc;
  }, {});

  return (
    <div className="gal-page">
      <Navbar />

      {/* ── HERO ── */}
      <section className="gal-hero">
        <div className="gal-hero__particles">
          {[...Array(6)].map((_, i) => (
            <div className="gal-hero__particle" key={i} style={{ '--i': i }} />
          ))}
        </div>
        <div className="gal-hero__content">
          <span className="gal-eyebrow">📸 Visual Chronicles</span>
          <h1>Our Gallery</h1>
          <p>Moments that define us — celebrations, achievements, learning, and life at school.</p>
          <div className="gal-hero__stats">
            <div className="gal-stat">
              <strong>{images.length}</strong>
              <span>Photos</span>
            </div>
            <div className="gal-stat-divider" />
            <div className="gal-stat">
              <strong>{[...new Set(images.map((i) => i.category))].length}</strong>
              <span>Albums</span>
            </div>
          </div>
        </div>
        <button className="gal-admin-btn" onClick={() => setShowAdminModal(true)}>
          {isLoggedIn ? '⚙️ Admin Panel' : '🔐 Admin Login'}
        </button>
      </section>

      {/* ── CATEGORY PILLS ── */}
      <section className="gal-cats">
        <div className="gal-cats__inner">
          {CATEGORY_OPTIONS.filter((c) => categoryCounts[c] > 0 || c === 'All').map((cat) => (
            <button
              key={cat}
              className={`cat-pill ${filterCategory === cat ? 'cat-pill--active' : ''}`}
              onClick={() => setFilterCategory(cat)}
            >
              {cat}
              <span className="cat-pill__count">{categoryCounts[cat]}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── TOOLBAR ── */}
      <div className="gal-toolbar">
        <div className="gal-search">
          <span>🔍</span>
          <input
            type="text"
            placeholder="Search photos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
        <div className="gal-toolbar__right">
          <span className="gal-result-count">
            {listLoading ? 'Loading...' : `${filtered.length} photo${filtered.length !== 1 ? 's' : ''}`}
          </span>
          <div className="view-toggle">
            <button
              className={viewMode === 'masonry' ? 'active' : ''}
              onClick={() => setViewMode('masonry')}
              title="Masonry"
            >⊞</button>
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="Grid"
            >▦</button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="List"
            >☰</button>
          </div>
        </div>
      </div>

      {/* ── GALLERY GRID ── */}
      <section className="gal-section">
        {listError && (
          <div className="gal-empty">
            <span>⚠️</span>
            <h3>Something went wrong</h3>
            <p>{listError}</p>
          </div>
        )}

        {!listError && listLoading && (
          <div className="gal-empty">
            <span>⏳</span>
            <h3>Loading gallery...</h3>
          </div>
        )}

        {!listError && !listLoading && filtered.length === 0 && (
          <div className="gal-empty">
            <span>🖼️</span>
            <h3>{images.length === 0 ? 'Gallery is Empty' : 'No Photos Found'}</h3>
            <p>
              {images.length === 0
                ? 'No images have been uploaded yet. Admin can upload photos from the Admin Panel.'
                : 'Try changing your filters or search term.'}
            </p>
          </div>
        )}

        {!listError && !listLoading && filtered.length > 0 && (
          <div className={`gal-grid gal-grid--${viewMode}`}>
            {filtered.map((img, i) => (
              <div
                className="gal-item"
                key={img.id}
                style={{ animationDelay: `${Math.min(i * 0.05, 0.5)}s` }}
                onClick={() => setLightboxIndex(i)}
              >
                <div className="gal-item__img-wrap">
                  <img src={img.filepath} alt={img.caption} loading="lazy" />
                  <div className="gal-item__overlay">
                    <span className="gal-item__zoom">🔍</span>
                    <span className="gal-item__caption-overlay">{img.caption}</span>
                  </div>
                </div>
                <div className="gal-item__info">
                  <span className="gal-item__cat">{img.category}</span>
                  <p className="gal-item__caption">{img.caption}</p>
                  <span className="gal-item__date">{formatDate(img.uploaded_at)}</span>
                  {isLoggedIn && (
                    <button
                      className="gal-item__delete"
                      onClick={(e) => { e.stopPropagation(); confirmDelete(img.id); }}
                    >🗑️ Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ══════════════════════════════════════
          LIGHTBOX
      ══════════════════════════════════════ */}
      {lightboxIndex !== null && filtered[lightboxIndex] && (
        <div className="lightbox" onClick={() => setLightboxIndex(null)}>
          <button className="lightbox__close" onClick={() => setLightboxIndex(null)}>✕</button>
          <button className="lightbox__nav lightbox__nav--prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}>‹</button>
          <div className="lightbox__inner" onClick={(e) => e.stopPropagation()}>
            <img src={filtered[lightboxIndex].filepath} alt={filtered[lightboxIndex].caption} />
            <div className="lightbox__meta">
              <span className="lightbox__cat">{filtered[lightboxIndex].category}</span>
              <p className="lightbox__caption">{filtered[lightboxIndex].caption}</p>
              <span className="lightbox__counter">{lightboxIndex + 1} / {filtered.length}</span>
            </div>
          </div>
          <button className="lightbox__nav lightbox__nav--next" onClick={(e) => { e.stopPropagation(); nextImage(); }}>›</button>
        </div>
      )}

      {/* ══════════════════════════════════════
          ADMIN MODAL
      ══════════════════════════════════════ */}
      {showAdminModal && (
        <div
          className="modal-overlay"
          onClick={(e) => { if (e.target.classList.contains('modal-overlay') && !isLoggedIn) setShowAdminModal(false); }}
        >
          <div className="modal">

            {/* Login screen */}
            {!isLoggedIn ? (
              <div className="modal-login">
                <button className="modal-close" onClick={() => setShowAdminModal(false)}>✕</button>
                <div className="modal-login__icon">🔐</div>
                <h2>Admin Login</h2>
                <p>Sign in to manage the gallery</p>
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    placeholder="Enter username"
                    value={loginUser}
                    onChange={(e) => setLoginUser(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    placeholder="Enter password"
                    value={loginPass}
                    onChange={(e) => setLoginPass(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  />
                </div>
                {loginError && <div className="form-error">{loginError}</div>}
                <button className="modal-login__btn" onClick={handleLogin} disabled={loginLoading}>
                  {loginLoading ? 'Logging in...' : 'Login →'}
                </button>
              </div>
            ) : (
              /* Admin Panel */
              <div className="modal-admin">
                <div className="modal-admin__header">
                  <div>
                    <h2>Gallery Admin</h2>
                    <p>Upload and manage school photos</p>
                  </div>
                  <div className="modal-admin__header-actions">
                    <span className="admin-badge">✅ {loginUser}</span>
                    <button className="btn-logout" onClick={handleLogout}>Logout</button>
                    <button className="modal-close" onClick={() => setShowAdminModal(false)}>✕</button>
                  </div>
                </div>

                <div className="modal-admin__body">
                  {/* Summary */}
                  <div className="admin-summary">
                    <div className="admin-summary__card">
                      <span>{images.length}</span>
                      <label>Total Photos</label>
                    </div>
                    <div className="admin-summary__card">
                      <span>{[...new Set(images.map((i) => i.category))].length}</span>
                      <label>Categories</label>
                    </div>
                    <div className="admin-summary__card">
                      <span>
                        {images.length > 0
                          ? formatDate(images.reduce((a, b) =>
                              new Date(a.uploaded_at) > new Date(b.uploaded_at) ? a : b
                            ).uploaded_at)
                          : '—'}
                      </span>
                      <label>Last Upload</label>
                    </div>
                  </div>

                  <h3 className="upload-heading">Upload New Photos</h3>

                  <div className="upload-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Category *</label>
                        <select
                          value={uploadCategory}
                          onChange={(e) => setUploadCategory(e.target.value)}
                        >
                          <option value="">Select Category</option>
                          {CATEGORY_OPTIONS.filter((c) => c !== 'All').map((c) => (
                            <option key={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Caption / Album Name</label>
                        <input
                          type="text"
                          placeholder="e.g. Annual Sports Day 2024"
                          value={uploadCaption}
                          onChange={(e) => setUploadCaption(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Drag & Drop Zone */}
                    <div
                      className={`drop-zone ${isDragging ? 'drop-zone--active' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleFileDrop}
                      onClick={() => fileRef.current.click()}
                    >
                      <input
                        type="file"
                        ref={fileRef}
                        style={{ display: 'none' }}
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        multiple
                        onChange={handleFileInput}
                      />
                      <span className="drop-zone__icon">🖼️</span>
                      <p><strong>Drag & drop images here</strong> or <span className="drop-zone__browse">click to browse</span></p>
                      <small>JPG, PNG, WEBP, GIF — max 8 MB each — multiple files supported</small>
                    </div>

                    {/* Pending previews */}
                    {pendingFiles.length > 0 && (
                      <div className="pending-grid">
                        {pendingFiles.map((file, idx) => (
                          <div className="pending-item" key={idx}>
                            <img src={URL.createObjectURL(file)} alt={file.name} />
                            <div className="pending-item__info">
                              <span>{file.name.length > 20 ? file.name.slice(0, 18) + '…' : file.name}</span>
                              <span>{formatFileSize(file.size)}</span>
                            </div>
                            <button
                              className="pending-item__remove"
                              onClick={() => removePending(idx)}
                            >✕</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {uploadError && <div className="form-error">{uploadError}</div>}
                    {uploadSuccess && <div className="form-success">{uploadSuccess}</div>}

                    <button
                      className="btn-upload"
                      onClick={handleUpload}
                      disabled={isUploading}
                    >
                      {isUploading
                        ? '⏳ Uploading...'
                        : `⬆ Upload ${pendingFiles.length > 0 ? pendingFiles.length + ' ' : ''}Photo${pendingFiles.length !== 1 ? 's' : ''}`}
                    </button>
                  </div>

                  {/* Manage existing */}
                  {images.length > 0 && (
                    <div className="admin-manage">
                      <h3>Manage Photos ({images.length})</h3>
                      <div className="admin-manage__grid">
                        {images.map((img) => (
                          <div className="admin-thumb" key={img.id}>
                            <img src={img.filepath} alt={img.caption} />
                            <div className="admin-thumb__overlay">
                              <span className="admin-thumb__cat">{img.category}</span>
                              <button
                                className="admin-thumb__del"
                                onClick={() => confirmDelete(img.id)}
                              >🗑️</button>
                            </div>
                            <p className="admin-thumb__caption">{img.caption}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal modal--confirm">
            <div className="confirm-icon">⚠️</div>
            <h3>Delete Photo?</h3>
            <p>This action cannot be undone.</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn-delete" onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;